/**
 * WebRTC transport for P2P transfer (browser layer).
 *
 * Establishes an RTCPeerConnection + DataChannel between sender and receiver using
 * ephemeral signaling from `/api/p2p/*`. ALL signaling payloads
 * (SDP/ICE) travel encrypted with the QR key (AAD per role), so the
 * server only relays opaque envelopes: it cannot read the SDP or inject its own
 * (without the key it cannot produce valid envelopes → MITM is impossible).
 *
 * No STUN/TURN servers by default (privacy: zero third parties): with both
 * devices on the same network (the QR case: they are next to each other),
 * host/mDNS candidates suffice. Different networks behind NAT = see the guide (Manual).
 */

import {
  decryptEnvelopeText,
  encryptEnvelope,
  isRecord,
  type SignalingClient,
  SignalingError,
  type TransferChannel,
} from "@listto/shared";

const POLL_INTERVAL_MS = 700;
const CONNECT_TIMEOUT_MS = 45_000;
const DATA_CHANNEL_LABEL = "listto-p2p-transfer";

/** Session description (what is set by `setRemoteDescription`). */
type SdpPayload = { type: "offer" | "answer"; sdp: string };

type SignalPayload = SdpPayload | { type: "ice"; candidate: RTCIceCandidateInit };

/** Validates the decrypted peer JSON (without assertions on external data). */
function parseSignalPayload(text: string): SignalPayload | null {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }
  if (!isRecord(value)) {
    return null;
  }
  if ((value.type === "offer" || value.type === "answer") && typeof value.sdp === "string") {
    return { type: value.type, sdp: value.sdp };
  }
  if (value.type === "ice" && isRecord(value.candidate)) {
    const c = value.candidate;
    const candidate: RTCIceCandidateInit = {
      candidate: typeof c.candidate === "string" ? c.candidate : undefined,
      sdpMid: typeof c.sdpMid === "string" ? c.sdpMid : null,
      sdpMLineIndex: typeof c.sdpMLineIndex === "number" ? c.sdpMLineIndex : null,
      usernameFragment: typeof c.usernameFragment === "string" ? c.usernameFragment : null,
    };
    return { type: "ice", candidate };
  }
  return null;
}

export interface RtcPeerHandle {
  channelPromise: Promise<TransferChannel>;
  close(): void;
}

function wrapDataChannel(dc: RTCDataChannel): TransferChannel {
  return {
    send: (data) => dc.send(data),
    get bufferedAmount() {
      return dc.bufferedAmount;
    },
    onMessage(handler) {
      dc.addEventListener("message", (event) => {
        handler(String(event.data));
      });
    },
    onClose(handler) {
      dc.addEventListener("close", () => {
        handler();
      });
    },
  };
}

interface PeerOptions {
  signaling: SignalingClient;
  code: string;
  token: string;
  key: CryptoKey;
  /** AAD with which we encrypt OUR data and the AAD with which we decrypt the peer's. */
  sendAad: "signal:sender" | "signal:receiver";
  receiveAad: "signal:sender" | "signal:receiver";
  onPeerJoined?: () => void;
  timeoutMs?: number;
}

/**
 * Shared loop: publishes our ICE, polls the inbox, and resolves
 * when the DataChannel opens. `close()` stops polling and closes the connection.
 */
function createPeer(
  options: PeerOptions,
  setup: (pc: RTCPeerConnection, onChannel: (dc: RTCDataChannel) => void) => Promise<void>,
  onSignal: (pc: RTCPeerConnection, payload: SdpPayload) => Promise<void>,
): RtcPeerHandle {
  const { signaling, code, token, key, sendAad, receiveAad } = options;
  const pc = new RTCPeerConnection();
  let stopped = false;
  // With the channel open, signaling is no longer needed: stop polling WITHOUT closing
  // the RTCPeerConnection (closing the room on the server makes the peer's next
  // fetch return 404; without this stop, that 404 would bring down a live connection).
  let pollingStopped = false;
  let pollTimer: ReturnType<typeof setTimeout> | undefined;

  const stopPolling = () => {
    pollingStopped = true;
    if (pollTimer !== undefined) {
      clearTimeout(pollTimer);
    }
  };

  const stop = () => {
    stopped = true;
    stopPolling();
    try {
      pc.close();
    } catch {
      // already closed
    }
  };

  const channelPromise = new Promise<TransferChannel>((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error("p2p-connect-timeout"));
      stop();
    }, options.timeoutMs ?? CONNECT_TIMEOUT_MS);

    const onChannel = (dc: RTCDataChannel) => {
      dc.addEventListener("open", () => {
        clearTimeout(timeout);
        stopPolling();
        // The room has served its purpose: close it on the server (hygiene and
        // single-use reinforcement). Best-effort.
        void signaling.closeRoom(code, token).catch(() => undefined);
        resolve(wrapDataChannel(dc));
      });
    };

    pc.addEventListener("icecandidate", (event) => {
      if (!event.candidate || stopped) {
        return;
      }
      const payload: SignalPayload = { type: "ice", candidate: event.candidate.toJSON() };
      void (async () => {
        try {
          await signaling.postMessage(
            code,
            token,
            await encryptEnvelope(key, JSON.stringify(payload), sendAad),
          );
        } catch {
          // the room may already be closed with the channel open: ignorable
        }
      })();
    });

    pc.addEventListener("connectionstatechange", () => {
      if (pc.connectionState === "failed") {
        clearTimeout(timeout);
        reject(new Error("p2p-connection-failed"));
        stop();
      }
    });

    // ─── Out-of-order ICE ──────────────────────────────────────────────────
    // The relay does NOT guarantee that the SDP arrives before the ICE candidates of
    // the sender: between `setLocalDescription()` (which starts gathering)
    // and the offer/answer POST there is an `await encryptEnvelope(...)`, so
    // an `icecandidate` can arrive earlier — and under CPU pressure (CI) it
    // does. Applying a candidate without `remoteDescription` throws InvalidStateError,
    // which the `poll()` catch interpreted as a FATAL signaling failure:
    // the transfer died with "Network error" before starting.
    // Reproduced manually delaying the sender's first POST by 800 ms: 100%
    // failure before this change, 100% success after.
    // Canonical WebRTC solution: queue candidates until `remoteDescription`
    // exists and drain them then. In addition, a candidate that cannot be
    // applied is best-effort (one less route), NEVER a reason to tear down
    // a session that might connect through another candidate.
    const pendingIce: RTCIceCandidateInit[] = [];
    const drainIce = async () => {
      if (pc.remoteDescription === null) {
        return;
      }
      while (pendingIce.length > 0) {
        const candidate = pendingIce.shift();
        if (candidate === undefined) {
          break;
        }
        try {
          // oxlint-disable-next-line no-await-in-loop
          await pc.addIceCandidate(candidate);
        } catch {
          // best-effort: that candidate does not work, others may work
        }
      }
    };

    let after = 0;
    let announcedPeer = false;
    const poll = async () => {
      if (stopped || pollingStopped) {
        return;
      }
      try {
        const inbox = await signaling.fetchMessages(code, token, after);
        if (inbox.peerJoined && !announcedPeer) {
          announcedPeer = true;
          options.onPeerJoined?.();
        }
        for (const message of inbox.messages) {
          after = Math.max(after, message.seq);
          // Invalid envelope (wrong key / tampering) → abort NOW.
          const payload = parseSignalPayload(
            await decryptEnvelopeText(key, message.payload, receiveAad),
          );
          if (!payload) {
            throw new Error("p2p-signaling-failed");
          }
          if (payload.type === "ice") {
            pendingIce.push(payload.candidate);
          } else {
            await onSignal(pc, payload);
          }
          await drainIce();
        }
      } catch (error) {
        if (!stopped && !pollingStopped) {
          clearTimeout(timeout);
          reject(error instanceof SignalingError ? error : new Error("p2p-signaling-failed"));
          stop();
        }
        return;
      }
      if (!stopped && !pollingStopped) {
        pollTimer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
      }
    };

    void (async () => {
      try {
        await setup(pc, onChannel);
        await poll();
      } catch (error) {
        clearTimeout(timeout);
        reject(error instanceof SignalingError ? error : new Error("p2p-setup-failed"));
        stop();
      }
    })();
  });

  // Avoids an "unhandled rejection" if the consumer only calls close() after a failure.
  channelPromise.catch(() => undefined);

  return { channelPromise, close: stop };
}

/** SENDER: creates the DataChannel, publishes the encrypted offer, and waits for the response. */
export function connectAsSender(options: {
  signaling: SignalingClient;
  code: string;
  senderToken: string;
  key: CryptoKey;
  onPeerJoined?: () => void;
  timeoutMs?: number;
}): RtcPeerHandle {
  const { signaling, code, senderToken, key } = options;
  return createPeer(
    {
      signaling,
      code,
      token: senderToken,
      key,
      sendAad: "signal:sender",
      receiveAad: "signal:receiver",
      onPeerJoined: options.onPeerJoined,
      timeoutMs: options.timeoutMs,
    },
    async (pc, onChannel) => {
      onChannel(pc.createDataChannel(DATA_CHANNEL_LABEL));
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const payload: SignalPayload = { type: "offer", sdp: offer.sdp ?? "" };
      await signaling.postMessage(
        code,
        senderToken,
        await encryptEnvelope(key, JSON.stringify(payload), "signal:sender"),
      );
    },
    // ICE candidates are applied by `createPeer` (queued until
    // `remoteDescription` exists); here only the peer's session description.
    async (pc, payload) => {
      if (payload.type === "answer") {
        await pc.setRemoteDescription({ type: "answer", sdp: payload.sdp });
      }
    },
  );
}

/** RECEIVER: waits for the encrypted offer, responds, and exposes the incoming channel. */
export function connectAsReceiver(options: {
  signaling: SignalingClient;
  code: string;
  receiverToken: string;
  key: CryptoKey;
  timeoutMs?: number;
}): RtcPeerHandle {
  const { signaling, code, receiverToken, key } = options;
  return createPeer(
    {
      signaling,
      code,
      token: receiverToken,
      key,
      sendAad: "signal:receiver",
      receiveAad: "signal:sender",
      timeoutMs: options.timeoutMs,
    },
    async (pc, onChannel) => {
      pc.addEventListener("datachannel", (event) => {
        onChannel(event.channel);
      });
    },
    // ICE candidates are applied by `createPeer` (queued until
    // `remoteDescription` exists); here only the peer's session description.
    async (pc, payload) => {
      if (payload.type === "offer") {
        await pc.setRemoteDescription({ type: "offer", sdp: payload.sdp });
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        const out: SignalPayload = { type: "answer", sdp: answer.sdp ?? "" };
        await signaling.postMessage(
          code,
          receiverToken,
          await encryptEnvelope(key, JSON.stringify(out), "signal:receiver"),
        );
      }
    },
  );
}
